import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Save } from 'lucide-react';

import DashboardLayout from '../components/layout/DashboardLayout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Separator } from '../components/ui/separator';
import {
  API,
  BUCKET_OPTIONS,
  COMPONENT_TYPE_OPTIONS,
  CURRENCY_OPTIONS,
  OPERATIONAL_STATUS_OPTIONS,
  SPARE_STATUS_OPTIONS,
  SCRAP_STATUS_OPTIONS,
  UNIT_OPTIONS,
  emptyComponentForm,
  normalizeUnit,
  normalizeComponentPayload,
} from '../lib/componentsModule';

export default function ComponentFormPage() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();

  const [form, setForm] = useState(emptyComponentForm);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(isEdit);
  const [assets, setAssets] = useState([]);
  const [components, setComponents] = useState([]);
  const [manufacturers, setManufacturers] = useState([]);

  const loadOptions = useCallback(async () => {
    try {
      const [assetsRes, componentsRes, manufacturersRes] = await Promise.all([
        axios.get(`${API}/assets`),
        axios.get(`${API}/components?limit=1000`),
        axios.get(`${API}/components/manufacturers`),
      ]);
      setAssets(assetsRes.data || []);
      setComponents(componentsRes.data?.items || []);
      setManufacturers(manufacturersRes.data || []);
    } catch (error) {
      toast.error('Failed to load selection data');
    }
  }, []);

  const loadComponent = useCallback(async () => {
    try {
      setFetching(true);
      const { data } = await axios.get(`${API}/components/${id}`);
      setForm({
        ...emptyComponentForm,
        ...data,
        unit: normalizeUnit(data.unit),
        manufacturer_id: data.manufacturer_id || 'none',
        asset_id: data.asset_id || 'none',
        parent_component_id: data.parent_component_id || 'none',
        quantity: data.quantity ?? 0,
      });
    } catch (error) {
      toast.error('Failed to load component details');
      navigate('/components');
    } finally {
      setFetching(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    loadOptions();
  }, [loadOptions]);

  useEffect(() => {
    if (isEdit) loadComponent();
  }, [isEdit, loadComponent]);

  const setField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const parentOptions = useMemo(() => {
    return components.filter((component) => {
      if (isEdit && component.id === id) return false;
      if (component.bucket !== 'operational') return false;
      if (form.asset_id === 'none') return false;
      return component.asset_id === form.asset_id;
    });
  }, [components, form.asset_id, isEdit, id]);

  const validate = () => {
    if (!form.name.trim()) return 'Name is required';
    if (!form.bucket) return 'Bucket type is required';
    if (form.bucket === 'operational' && !form.status) return 'Operational status is required';
    if (form.bucket === 'scrap_recyclable' && !form.status) return 'Scrap/Recyclable status is required';
    if (form.bucket === 'spare' && (form.safety_quantity === '' || form.safety_quantity === null)) {
      return 'Safety quantity is required for spare parts';
    }
    return null;
  };

  const handleSubmit = async () => {
    const validationError = validate();
    if (validationError) {
      toast.error(validationError);
      return;
    }

    const payload = normalizeComponentPayload(form);

    try {
      setLoading(true);
      if (isEdit) {
        await axios.patch(`${API}/components/${id}`, payload);
        toast.success('Component updated');
      } else {
        await axios.post(`${API}/components`, payload);
        toast.success('Component created');
      }
      navigate('/components');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save component');
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin w-8 h-8 border-4 border-sky-500 border-t-transparent rounded-full" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={() => navigate('/components')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">{isEdit ? 'Edit Component' : 'Add Component'}</h1>
              <p className="text-slate-500">Fill the component details by bucket type</p>
            </div>
          </div>

          <Button onClick={handleSubmit} disabled={loading}>
            <Save className="w-4 h-4 mr-2" />
            {loading ? 'Saving...' : 'Save'}
          </Button>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Component Bucket</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="max-w-sm space-y-2">
              <Label>Bucket Type</Label>
              <Select
                value={form.bucket}
                onValueChange={(value) => {
                  const status = value === 'scrap_recyclable' ? 'to_be_recycled' : 'active';
                  setForm((prev) => ({ ...prev, bucket: value, status }));
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select bucket" />
                </SelectTrigger>
                <SelectContent>
                  {BUCKET_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>Common Fields</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Name *</Label>
                <Input value={form.name} onChange={(e) => setField('name', e.target.value)} placeholder="Component name" />
              </div>

              <div className="space-y-2">
                <Label>Type</Label>
                <Select value={form.type} onValueChange={(value) => setField('type', value)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {COMPONENT_TYPE_OPTIONS.map((type) => (
                      <SelectItem key={type} value={type}>{type.replaceAll('_', ' ')}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Serial Number</Label>
                <Input value={form.serial_number || ''} onChange={(e) => setField('serial_number', e.target.value)} placeholder="SN-001" />
              </div>

              <div className="space-y-2">
                <Label>Manufacturer</Label>
                <Select
                  value={form.manufacturer_id || 'none'}
                  onValueChange={(value) => {
                    if (value === 'none') {
                      setForm((prev) => ({ ...prev, manufacturer_id: 'none', manufacturer: '' }));
                      return;
                    }
                    const selected = manufacturers.find((m) => m.id === value);
                    setForm((prev) => ({
                      ...prev,
                      manufacturer_id: value,
                      manufacturer: selected?.name || '',
                    }));
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select manufacturer" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {manufacturers.map((manufacturer) => (
                      <SelectItem key={manufacturer.id} value={manufacturer.id}>
                        {manufacturer.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Model</Label>
                <Input value={form.model || ''} onChange={(e) => setField('model', e.target.value)} placeholder="Model" />
              </div>

              <div className="space-y-2">
                <Label>OEM Datasheet URL</Label>
                <Input value={form.oem_datasheet_url || ''} onChange={(e) => setField('oem_datasheet_url', e.target.value)} placeholder="https://" />
              </div>
            </div>

            <Separator />

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label>Quantity</Label>
                <Input type="number" value={form.quantity ?? 0} onChange={(e) => setField('quantity', e.target.value)} />
              </div>

              <div className="space-y-2">
                <Label>Unit</Label>
                <Select value={form.unit} onValueChange={(value) => setField('unit', value)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {UNIT_OPTIONS.map((unit) => (
                      <SelectItem key={unit} value={unit}>{unit}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Asset</Label>
                <Select value={form.asset_id || 'none'} onValueChange={(value) => {
                  setField('asset_id', value);
                  if (value === 'none') {
                    setField('parent_component_id', 'none');
                  }
                }}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select asset" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {assets.map((asset) => (
                      <SelectItem key={asset.id} value={asset.id}>{asset.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={form.status || ''} onValueChange={(value) => setField('status', value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    {(form.bucket === 'operational'
                      ? OPERATIONAL_STATUS_OPTIONS
                      : form.bucket === 'spare'
                        ? SPARE_STATUS_OPTIONS
                        : SCRAP_STATUS_OPTIONS).map((status) => (
                      <SelectItem key={status} value={status}>{status.replaceAll('_', ' ')}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea value={form.notes || ''} onChange={(e) => setField('notes', e.target.value)} placeholder="Additional details" rows={4} />
            </div>
          </CardContent>
        </Card>

        {form.bucket === 'operational' && (
          <Card>
            <CardHeader><CardTitle>Operational Fields</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Installation Date</Label>
                  <Input type="date" value={form.installation_date || ''} onChange={(e) => setField('installation_date', e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label>Warranty Expiry Date</Label>
                  <Input type="date" value={form.warranty_expiry_date || ''} onChange={(e) => setField('warranty_expiry_date', e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label>Last Preventive Maintenance Date</Label>
                  <Input type="date" value={form.last_pm_date || ''} onChange={(e) => setField('last_pm_date', e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label>Next Preventive Maintenance</Label>
                  <Input type="date" value={form.next_pm_date || ''} onChange={(e) => setField('next_pm_date', e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label>Parent Component</Label>
                  <Select value={form.parent_component_id || 'none'} onValueChange={(value) => setField('parent_component_id', value)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Optional parent" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {parentOptions.map((component) => (
                        <SelectItem key={component.id} value={component.id}>{component.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Tilt</Label>
                  <Input type="number" value={form.tilt ?? ''} onChange={(e) => setField('tilt', e.target.value)} placeholder="0" />
                </div>
                <div className="space-y-2">
                  <Label>Azimuth</Label>
                  <Input type="number" value={form.azimuth ?? ''} onChange={(e) => setField('azimuth', e.target.value)} placeholder="0" />
                </div>
                <div className="space-y-2">
                  <Label>DC Capacity (kW)</Label>
                  <Input type="number" value={form.dc_capacity_kw ?? ''} onChange={(e) => setField('dc_capacity_kw', e.target.value)} placeholder="0" />
                </div>
                <div className="space-y-2">
                  <Label>AC Capacity (kW)</Label>
                  <Input type="number" value={form.ac_capacity_kw ?? ''} onChange={(e) => setField('ac_capacity_kw', e.target.value)} placeholder="0" />
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {form.bucket === 'spare' && (
          <Card>
            <CardHeader><CardTitle>Spare Fields</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Safety (Contractual) Quantity *</Label>
                  <Input type="number" value={form.safety_quantity ?? ''} onChange={(e) => setField('safety_quantity', e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label>Expiry Date</Label>
                  <Input type="date" value={form.expiry_date || ''} onChange={(e) => setField('expiry_date', e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label>Manufacturing Date</Label>
                  <Input type="date" value={form.manufacturing_date || ''} onChange={(e) => setField('manufacturing_date', e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label>Last Maintenance Date</Label>
                  <Input type="date" value={form.last_maintenance_date || ''} onChange={(e) => setField('last_maintenance_date', e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label>Next Maintenance Date</Label>
                  <Input type="date" value={form.next_maintenance_date || ''} onChange={(e) => setField('next_maintenance_date', e.target.value)} />
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {form.bucket === 'scrap_recyclable' && (
          <Card>
            <CardHeader><CardTitle>Scrap / Recyclable Fields</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Recycled Date</Label>
                  <Input type="date" value={form.recycled_date || ''} onChange={(e) => setField('recycled_date', e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label>Rycycling Company</Label>
                  <Input value={form.recycled_name || ''} onChange={(e) => setField('recycled_name', e.target.value)} placeholder="Recycler / buyer" />
                </div>
                <div className="space-y-2">
                  <Label>Credited Amount</Label>
                  <Input type="number" value={form.amount_received ?? ''} onChange={(e) => setField('amount_received', e.target.value)} placeholder="0" />
                </div>
                <div className="space-y-2">
                  <Label>Currency</Label>
                  <Select value={form.amount_currency || 'USD'} onValueChange={(value) => setField('amount_currency', value)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CURRENCY_OPTIONS.map((currency) => (
                        <SelectItem key={currency} value={currency}>{currency}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
}
